import { FC, memo, useCallback, useMemo, useState } from 'react';
import { ButtonWrapper } from '@tempusfinance/common-ui';
import { DecimalFormat } from '@tempusfinance/decimal';
import Typography from '../Typography';
import Icon from '../Icon';
import ModalWrapper from '../ModalWrapper';
import BaseInput from '../BaseInput';

import './Slippage.scss';
import { Decimal } from '../../../datastructures';

interface SlippageProps {
  value: string;
  variant?: 'primary' | 'secondary';
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const SlippageComponent: FC<SlippageProps> = ({ value, variant = 'primary', onChange, onBlur }) => {
  const [open, setOpen] = useState(false);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const slippage = useMemo(() => {
    return Decimal.parse(value, 0);
  }, [value]);

  const slippageFormatted = useMemo(() => {
    return DecimalFormat.format(slippage.div(100), {
      style: 'percentage',
      pad: true,
      fractionDigits: 2,
    });
  }, [slippage]);

  return (
    <>
      <ButtonWrapper onClick={onOpen}>
        <div className="nostra__slippage" data-variant={variant}>
          <Icon variant="slippage" color="supporting" size={14} />
          {variant === 'primary' && (
            <Typography variant="input-body-sm" color="text-supporting" weight="bold">
              {slippageFormatted}
            </Typography>
          )}
        </div>
      </ButtonWrapper>
      <ModalWrapper open={open} onClose={onClose}>
        <div className="nostra__slippage__modal">
          <Typography variant="heading-sm" weight="medium">
            Max slippage
          </Typography>
          <div className="nostra__slippage__modalInputContainer">
            <div className="nostra__slippage__modalInput">
              <BaseInput value={value} onChange={onChange} pattern="[0-9]*[.]?[0-9]{0,2}" onBlur={onBlur} />
              <Icon variant="percent" size={22} color="supporting" />
            </div>
            <div className="nostra__slippage__modalInputActions">
              <ButtonWrapper
                onClick={() => {
                  onChange('0.1');
                }}
              >
                <div className="nostra__slippage__modalInputAction" data-selected={slippage.equals(0.1)}>
                  <Typography
                    variant="button-sm"
                    weight={slippage.equals(0.1) ? 'semi-bold' : 'regular'}
                    color={slippage.equals(0.1) ? 'text-primary-darkbg' : 'text-supporting'}
                  >
                    0.1%
                  </Typography>
                </div>
              </ButtonWrapper>
              <ButtonWrapper
                onClick={() => {
                  onChange('0.5');
                }}
              >
                <div className="nostra__slippage__modalInputAction" data-selected={slippage.equals(0.5)}>
                  <Typography
                    variant="button-sm"
                    weight={slippage.equals(0.5) ? 'semi-bold' : 'regular'}
                    color={slippage.equals(0.5) ? 'text-primary-darkbg' : 'text-supporting'}
                  >
                    0.5%
                  </Typography>
                </div>
              </ButtonWrapper>
              <ButtonWrapper
                onClick={() => {
                  onChange('1');
                }}
              >
                <div className="nostra__slippage__modalInputAction" data-selected={slippage.equals(1)}>
                  <Typography
                    variant="button-sm"
                    weight={slippage.equals(1) ? 'semi-bold' : 'regular'}
                    color={slippage.equals(1) ? 'text-primary-darkbg' : 'text-supporting'}
                  >
                    1%
                  </Typography>
                </div>
              </ButtonWrapper>
            </div>
          </div>
          {slippage.gte(5) && (
            <div className="nostra__slippage__modalWarning">
              <Icon variant="alert" size={14} />
              <Typography variant="body-sm" color="text-error">
                High slippage could lead to a large price impact for your trade.
              </Typography>
            </div>
          )}
        </div>
      </ModalWrapper>
    </>
  );
};
export const Slippage = memo(SlippageComponent);
